import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mic, Square, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { speak } from "@/lib/buddy";

const LABELS = { clarity: "Clarity", confidence: "Confidence", structure: "Structure", call_to_action: "Call to action" };

const Feedback = ({ p, voice }) => (
  <article className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6 rise" data-testid={`pitch-${p.id}`}>
    <div className="flex flex-wrap justify-between gap-3">
      <p className="text-xs text-stone-500">{new Date(p.created_at).toLocaleString()} · {p.metrics.seconds}s · {p.metrics.wpm ?? "-"} words/min · {p.metrics.filler_count} filler words</p>
      <button onClick={() => speak(p.spoken_feedback, voice).catch(() => toast.error("Voice unavailable"))} data-testid={`pitch-listen-${p.id}`} className="inline-flex items-center gap-1.5 text-sm text-[#6B9080]"><Volume2 className="w-4 h-4" /> Hear feedback</button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
      {Object.entries(LABELS).map(([k, l]) => (
        <div key={k} className="rounded-2xl bg-[#F7F3EB] p-3" data-testid={`pitch-score-${k}`}><p className="text-[11px] uppercase tracking-widest text-stone-500">{l}</p>
          <p className="font-display text-2xl text-[#2C4A3E]">{p.scores[k]}<span className="text-sm text-stone-400">/5</span></p></div>
      ))}
    </div>
    <p className="font-display text-[#2C4A3E] mt-4">{p.spoken_feedback}</p>
    <div className="grid md:grid-cols-2 gap-4 mt-4 text-sm">
      <div><p className="text-xs uppercase tracking-widest text-stone-500">Strengths</p><ul className="mt-1 space-y-1">{p.strengths.map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
      <div><p className="text-xs uppercase tracking-widest text-stone-500">Try next time</p><ul className="mt-1 space-y-1" data-testid="pitch-tips">{p.tips.map((s, i) => <li key={i}>• {s}</li>)}</ul></div>
    </div>
    {p.improved_opening && <p className="mt-4 text-sm italic text-[#C87D53]">Stronger opening: "{p.improved_opening}"</p>}
    <details className="mt-4 text-sm text-stone-600"><summary className="cursor-pointer">Transcript{p.metrics.fillers.length > 0 && ` (fillers: ${p.metrics.fillers.join(", ")})`}</summary><p className="mt-2">{p.transcript}</p></details>
  </article>
);

export default function PitchPractice() {
  const { buddy } = useProfile();
  const coach = buddy?.name || "Aria";
  const [list, setList] = useState([]);
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);
  const t0 = useRef(0);
  const timer = useRef(null);
  useEffect(() => { http.get("/pathway/pitches").then((r) => setList(r.data)).catch(() => {}); return () => clearInterval(timer.current); }, []);

  const upload = async (blob, seconds, type) => {
    setState("busy");
    const fd = new FormData();
    fd.append("file", blob, type.includes("mp4") ? "pitch.mp4" : "pitch.webm");
    fd.append("seconds", String(seconds));
    try {
      const r = await http.post("/pathway/pitch", fd);
      setList((l) => [r.data, ...l]);
      speak(r.data.spoken_feedback, buddy?.voice).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    setState("idle");
  };
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const r = new MediaRecorder(stream);
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); clearInterval(timer.current); upload(new Blob(chunks, { type: r.mimeType || "audio/webm" }), (Date.now() - t0.current) / 1000, r.mimeType || ""); };
      rec.current = r; t0.current = Date.now(); setSecs(0);
      timer.current = setInterval(() => { const s = Math.round((Date.now() - t0.current) / 1000); setSecs(s); if (s >= 180) r.state === "recording" && r.stop(); }, 500);
      r.start(); setState("recording");
    } catch { toast.error("Microphone access is needed to record your pitch."); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="pitch-page">
      <Link to="/pathway" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="pitch-back-link"><ArrowLeft className="w-4 h-4" /> Career Pathway</Link>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-6">Pitch Practice</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">Say your 1-3 minute business pitch out loud. {coach} listens, then scores clarity, confidence (pace and filler words), structure and your call to action, and tells you how to make it shine.</p>
      <section className="mt-8 rounded-[2rem] bg-[#2C4A3E] text-white p-8 flex flex-wrap items-center gap-6">
        <button onClick={() => (state === "recording" ? rec.current.stop() : state === "idle" && start())} disabled={state === "busy"} data-testid="pitch-record-btn" data-state={state}
          className={`w-20 h-20 rounded-full grid place-items-center shrink-0 transition-transform hover:scale-105 ${state === "recording" ? "bg-[#C87D53] animate-pulse" : "bg-white text-[#2C4A3E]"}`}>
          {state === "busy" ? <Loader2 className="w-8 h-8 animate-spin" /> : state === "recording" ? <Square className="w-7 h-7" /> : <Mic className="w-8 h-8" />}
        </button>
        <div>
          <p className="font-display text-2xl" data-testid="pitch-status">{state === "recording" ? `Recording… ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}` : state === "busy" ? `${coach} is listening to your pitch…` : "Tap to start your pitch"}</p>
          <p className="text-sm text-white/70 mt-1">Hook → problem → your solution → who it's for → your ask. Max 3 minutes.</p>
        </div>
      </section>
      <div className="space-y-5 mt-10" data-testid="pitch-history">
        {list.map((p) => <Feedback key={p.id} p={p} voice={buddy?.voice} />)}
        {!list.length && <p className="text-sm text-stone-500" data-testid="pitch-empty">No pitches yet. Your first one is the bravest.</p>}
      </div>
    </div>
  );
}
